import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDeptStatus } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';

/* ═══════════════════════════════════════════════════════════════════════════
   Department Status (/dept-status) — Finance
   Budget variance by department (= CE phase) per project, plus booked-vs-current
   gross margin. Replaces the hand-maintained BOMOCO.xlsx; see
   server/routes/deptStatus.js for the recovered formulas and their verification.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Whole dollars, no cents — matching the workbook's "$"#,##0 — and parentheses
   for under-budget, which is how the finance team reads a negative already. */
const fmt$ = (n) => {
  const v = Math.round(Number(n) || 0);
  const s = Math.abs(v).toLocaleString('en-US');
  return v < 0 ? `(${s})` : s;
};
const fmt$full = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const fmtPts = (n) => (n == null ? '–' : `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(1)}`);
const fmtPct = (n) => (n == null ? '–' : `${n.toFixed(1)}%`);

/* Over budget is bad and gets the alarm colour; under budget is good but quiet —
   most rows are under, so colouring them all green would be noise. */
const varTone = (v) => (v > 0.5 ? 'var(--danger)' : v < -0.5 ? 'var(--text-2)' : 'var(--text-4)');
const gmTone = (d) => (d == null ? 'var(--text-4)' : d < -0.05 ? 'var(--danger)' : d > 0.05 ? 'var(--success)' : 'var(--text-2)');

/* ⚠️ Column budget, measured. A row gets `content − 38` px, i.e. ~1174 at a 1512
   viewport (see project_summit_laptop_layout: content = viewport − 300). Nine
   department columns do not fit, so the five with real activity get their own and
   the rest fold into "Other". The fold is computed from the DATA, not hardcoded,
   so if RCx ever becomes common it earns a column on its own. */
/* Header text, not the amounts, is what sizes a department column: "CONSTRUCTION" is
   12 uppercase chars against figures of about 9. Abbreviating the two longest bought
   the last 9px the table was over by. Full name stays in the column tooltip. */
const SHORT_LABEL = { Construction: 'Constr.', 'E3 Lighting': 'Lighting', 'Perf Mgmt': 'Perf' };
const shortOf = (label) => SHORT_LABEL[label] || label;

const MAX_DEPT_COLS = 5;
const DEPT_W = 84;

/* ⚠️ These widths were WRONG on the first pass. I added them up by hand, dropped the
   Unalloc column from the sum, and shipped a table needing 1256px into the ~1174 a row
   actually gets — so Current and Delta GM were off-screen. Exactly the defect fixed on
   Job Costs this morning, made again the same way: by trusting arithmetic instead of
   measuring. TABLE_MIN is now DERIVED from these values (1152), so the minWidth and the
   columns cannot drift apart, and the rendered widths were checked in the browser.

   ⚠️ SECOND lesson from the same table: these w values are only a HINT. The table is
   table-layout:auto, so a nowrap cell wider than its declared width expands the column
   regardless -- "$19,726,152" wants ~110px against a declared 96. That is why the first
   trim still clipped by 92px. What actually bought the space was cell PADDING (10px ->
   6px across 13 columns = ~104px) plus a half-point off the mono size. Measure the
   rendered scrollWidth; never trust these numbers as the real total. */
const W = { proj: 196, contract: 96, other: 76, unalloc: 86, overall: 96, gm: 62, delta: 58 };
// Real total, computed not guessed. A row gets content-38 ~= 1174px at 1512.
const TABLE_MIN = W.proj + W.contract + MAX_DEPT_COLS * DEPT_W + W.other + W.unalloc
  + W.overall + W.gm * 2 + W.delta;

const Th = ({ children, w, align = 'right', title, tone }) => (
  <th
    title={title}
    style={{
      width: w, minWidth: w, padding: '8px 5px', textAlign: align, whiteSpace: 'nowrap',
      fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase',
      color: tone || 'var(--text-3)', background: 'var(--bg-surface-2)',
      borderBottom: '1px solid var(--border-default)', position: 'sticky', top: 0, zIndex: 1,
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = 'right', mono = true, color, w, title, weight }) => (
  <td
    title={title}
    className={mono ? 'mono' : undefined}
    style={{
      width: w, padding: '8px 5px', textAlign: align, whiteSpace: 'nowrap',
      fontSize: mono ? 12.5 : 13, fontWeight: weight || (mono ? 600 : 400),
      fontVariantNumeric: 'tabular-nums', letterSpacing: mono ? '-0.01em' : undefined,
      color: color || 'var(--text-1)', borderBottom: '1px solid var(--border-subtle)',
    }}
  >
    {children}
  </td>
);

const DepartmentStatusPage = ({ onOpenProject }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('gm');
  const [bucket, setBucket] = useState('all');
  const [filterPM, setFilterPM] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      setData(await getDeptStatus());
    } catch (e) {
      console.error('Department status fetch failed:', e);
      setError(e.message || 'Failed to load the department status report');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data && data.rows) || [];
  const departments = (data && data.departments) || [];

  /* Which departments earn a column: the ones with the most jobs showing activity.
     Everything else folds into "Other", with the folded names in its tooltip so the
     number is never anonymous. */
  const { cols, folded } = useMemo(() => {
    const counts = {};
    departments.forEach((d) => { counts[d.phase] = 0; });
    rows.forEach((r) => departments.forEach((d) => {
      if ((r.departments[d.phase] || 0) !== 0) counts[d.phase] += 1;
    }));
    const ranked = [...departments].sort((a, b) => counts[b.phase] - counts[a.phase]);
    const keep = ranked.slice(0, MAX_DEPT_COLS)
      .filter((d) => counts[d.phase] > 0)
      // put them back in phase order so the columns read like the workbook
      .sort((a, b) => Number(a.phase) - Number(b.phase));
    const keepSet = new Set(keep.map((d) => d.phase));
    return { cols: keep, folded: departments.filter((d) => !keepSet.has(d.phase)) };
  }, [rows, departments]);

  const otherOf = useCallback(
    (r) => folded.reduce((s, d) => s + (r.departments[d.phase] || 0), 0),
    [folded],
  );

  const pmOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => (r.project_manager || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return ['', ...[...set].sort()];
  }, [rows]);

  const visible = useMemo(() => {
    let out = rows;
    if (filterPM) {
      out = out.filter((r) => (r.project_manager || '').split(',').map((s) => s.trim()).includes(filterPM));
    }
    if (bucket === 'eroding') out = out.filter((r) => r.gm_delta != null && r.gm_delta < -0.05);
    else if (bucket === 'improving') out = out.filter((r) => r.gm_delta != null && r.gm_delta > 0.05);
    else if (bucket === 'over') out = out.filter((r) => r.overall > 0.5);
    const s = [...out];
    if (sortBy === 'gm') s.sort((a, b) => (a.gm_delta == null ? 1 : b.gm_delta == null ? -1 : a.gm_delta - b.gm_delta));
    else if (sortBy === 'overall') s.sort((a, b) => b.overall - a.overall);
    else if (sortBy === 'contract') s.sort((a, b) => b.contract - a.contract);
    else if (sortBy === 'unallocated') s.sort((a, b) => Math.abs(b.unallocated) - Math.abs(a.unallocated));
    else s.sort((a, b) => String(a.project_name).localeCompare(String(b.project_name)));
    return s;
  }, [rows, filterPM, bucket, sortBy]);

  /* Totals over the VISIBLE set, so the footer always reflects the filter. GM is
     revenue-weighted (total margin ÷ total contract) over contract-bearing jobs
     only — never a mean of per-project percentages. Same rule as Job Costs. */
  const totals = useMemo(() => {
    const t = { contract: 0, overall: 0, unallocated: 0, other: 0, depts: {} };
    cols.forEach((d) => { t.depts[d.phase] = 0; });
    let cBudget = 0, cBase = 0;
    visible.forEach((r) => {
      t.contract += r.contract; t.overall += r.overall; t.unallocated += r.unallocated;
      t.other += otherOf(r);
      cols.forEach((d) => { t.depts[d.phase] += r.departments[d.phase] || 0; });
      if (r.contract > 0) { cBudget += r.budget; cBase += r.cost + (r.committed || 0); }
    });
    t.booked = t.contract > 0 ? ((t.contract - cBudget) / t.contract) * 100 : null;
    t.current = t.contract > 0 ? ((t.contract - cBase) / t.contract) * 100 : null;
    t.delta = t.booked == null ? null : t.current - t.booked;
    return t;
  }, [visible, cols, otherOf]);

  const counts = useMemo(() => ({
    all: rows.length,
    eroding: rows.filter((r) => r.gm_delta != null && r.gm_delta < -0.05).length,
    improving: rows.filter((r) => r.gm_delta != null && r.gm_delta > 0.05).length,
    over: rows.filter((r) => r.overall > 0.5).length,
  }), [rows]);

  if (loading) return <LoadingState message="Loading department status…" />;
  if (error) {
    return (
      <StateScreen
        kind="server-error"
        title="Couldn’t load the department status report"
        body="Something went wrong reading the job-cost data. Check your connection and try again."
        primary={{ label: 'Retry', onClick: load }}
      />
    );
  }
  if (!rows.length) {
    return (
      <StateScreen
        kind="empty"
        title="No job-cost data yet"
        body="Once ComputerEase job costs sync for the active roster, budget variance by department will appear here."
      />
    );
  }

  const sel = {
    height: 32, padding: '0 30px 0 10px', borderRadius: 8, fontSize: 13.5, fontWeight: 500,
    color: 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer',
    appearance: 'none', WebkitAppearance: 'none',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        .ds-row:hover td { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .ds-plink { background: none; border: none; padding: 0; cursor: pointer; font: inherit;
                    color: inherit; text-align: left; }
        .ds-plink:hover { color: var(--accent-text); }
        @media (min-width: 1340px) { .ds-th, .ds-td { white-space: nowrap; } }
      `}</style>

      {/* Filter bar — same shape as Job Costs / AR so the Finance screens feel alike. */}
      <section style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
        <SegmentedControl
          value={bucket}
          onChange={setBucket}
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'eroding', label: 'Margin eroding', count: counts.eroding },
            { value: 'improving', label: 'Improving', count: counts.improving },
            { value: 'over', label: 'Over budget', count: counts.over },
          ]}
        />
        <select value={filterPM} onChange={(e) => setFilterPM(e.target.value)} style={sel}>
          {pmOptions.map((n) => <option key={n || 'all'} value={n}>{n || 'All PMs'}</option>)}
        </select>
        <div style={{ flex: 1, minWidth: 8 }} />
        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort</span>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={sel}>
          <option value="gm">Margin change</option>
          <option value="overall">Most over budget</option>
          <option value="contract">Contract size</option>
          <option value="unallocated">Unallocated</option>
          <option value="name">Project name</option>
        </select>
      </section>

      {/* The basis note. Duncan asked for this explicitly: this report intentionally
          reads a different aggregate than the Job Costs tab, and two Finance screens
          disagreeing by design needs saying on the screen, not just in a commit. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 12px', fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
        <span aria-hidden="true" style={{ color: 'var(--text-3)' }}>ⓘ</span>
        <span>
          Departments are ComputerEase <strong>phases</strong>. Amounts are budget variance —
          over budget in red, under budget in parentheses.{' '}
          <strong>Unallocated</strong> is cost in the job total that no phase row accounts
          for; the spreadsheet this replaces folded it into Construction.
          {folded.length > 0 && <> <strong>Other</strong> combines {folded.map((d) => d.label).join(', ')}.</>}
        </span>
      </div>

      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: TABLE_MIN, borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <Th align="left" w={W.proj}>Project</Th>
                <Th w={W.contract}>Contract</Th>
                {cols.map((d) => <Th key={d.phase} w={DEPT_W} title={`${d.label} — CE phase ${d.phase}`}>{shortOf(d.label)}</Th>)}
                {folded.length > 0 && (
                  <Th w={W.other} title={`Combines: ${folded.map((f) => `${f.label} (phase ${f.phase})`).join(', ')}`}>Other</Th>
                )}
                <Th w={W.unalloc} title="Cost in the job total that no phase row accounts for">Unalloc.</Th>
                <Th w={W.overall}>Overall</Th>
                <Th w={W.gm} title="(contract − budget) ÷ contract">Booked</Th>
                <Th w={W.gm} title="(contract − cost − committed) ÷ contract">Current</Th>
                <Th w={W.delta} title="Current − Booked, in percentage points">Δ GM</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const other = otherOf(r);
                return (
                  <tr key={r.job_id} className="ds-row">
                    <Td align="left" mono={false} w={W.proj}>
                      <button
                        type="button"
                        className="ds-plink"
                        onClick={() => onOpenProject && onOpenProject(r.slug || r.job_id)}
                        title={r.project_manager ? `PM: ${r.project_manager}` : undefined}
                      >
                        <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: W.proj - 20 }}>{r.project_name}</span>
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>#{r.project_number}</span>
                      </button>
                    </Td>
                    <Td w={W.contract}>{fmt$full(r.contract)}</Td>
                    {cols.map((d) => {
                      const v = r.departments[d.phase] || 0;
                      return <Td key={d.phase} w={DEPT_W} color={varTone(v)}>{v === 0 ? '—' : fmt$(v)}</Td>;
                    })}
                    {folded.length > 0 && <Td w={W.other} color={varTone(other)}>{other === 0 ? '—' : fmt$(other)}</Td>}
                    <Td w={W.unalloc} color={Math.abs(r.unallocated) > 0.5 ? 'var(--warning)' : 'var(--text-4)'}>
                      {Math.abs(r.unallocated) < 0.5 ? '—' : fmt$(r.unallocated)}
                    </Td>
                    <Td w={W.overall} color={varTone(r.overall)} weight={700}>{fmt$(r.overall)}</Td>
                    <Td w={W.gm} color="var(--text-2)">{fmtPct(r.booked_gm)}</Td>
                    <Td w={W.gm} color="var(--text-2)">{fmtPct(r.current_gm)}</Td>
                    <Td w={W.delta} color={gmTone(r.gm_delta)} weight={700}>{fmtPts(r.gm_delta)}</Td>
                  </tr>
                );
              })}
              <tr>
                <Td align="left" mono={false} w={W.proj} weight={700}>
                  {visible.length} {visible.length === 1 ? 'job' : 'jobs'}
                </Td>
                <Td w={W.contract} weight={700}>{fmt$full(totals.contract)}</Td>
                {cols.map((d) => <Td key={d.phase} w={DEPT_W} weight={700} color={varTone(totals.depts[d.phase])}>{fmt$(totals.depts[d.phase])}</Td>)}
                {folded.length > 0 && <Td w={W.other} weight={700} color={varTone(totals.other)}>{fmt$(totals.other)}</Td>}
                <Td w={W.unalloc} weight={700} color="var(--warning)">{fmt$(totals.unallocated)}</Td>
                <Td w={W.overall} weight={700} color={varTone(totals.overall)}>{fmt$(totals.overall)}</Td>
                <Td w={W.gm} weight={700} color="var(--text-2)">{fmtPct(totals.booked)}</Td>
                <Td w={W.gm} weight={700} color="var(--text-2)">{fmtPct(totals.current)}</Td>
                <Td w={W.delta} weight={700} color={gmTone(totals.delta)}>{fmtPts(totals.delta)}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepartmentStatusPage;
